import ActionButton from "@/components/buttons/ActionButton";
import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import MarketingConsentSelect from "@/components/contact/MarketingConsentSelect";
import ContactsEmailAutoComplete from "@/components/form/components/ContactsEmailAutoComplete";
import { ContactsPhoneNumberAutoComplete } from "@/components/form/components/ContactsPhoneNumberAutoComplete";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { selectStandardProps } from "@/utils/componentUtils";
import { phoneNumberNormalizeFunction, resolveFormValidationError } from "@/utils/formUtils";
import { parseBirthDateFromPin } from "@/utils/utils";
import { validationFunctions, validations } from "@/utils/validationUtils";
import { Card, Col, Divider, Form, Input, Row, Select, Space } from "antd";
import { FormInstance, Rule } from "antd/lib/form";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import t from "../../../../../app/i18n";
import { rowGutter } from "../../../../../common/constants";
import { FieldConstraintViolation } from "../../../../../common/types";
import ClientDrawerForm from "../../../../client/components/drawers/ClientDrawerForm";
import ClientRepresentativeSelectModal from "../../../../client/components/search/ClientRepresentativeSelectModal";
import ClientSearchInput from "../../../../client/components/search/ClientSearchInput";
import {
  ClientFormStage,
  ClientRepresentativeFunction,
  ClientSearchActionType,
  ClientType,
} from "../../../../client/enums";
import { useClientSearch } from "../../../../client/hooks";
import { Client, ClientBase, ClientRepresentative, LegalClient, NaturalClient } from "../../../../client/types";
import { formatClientName } from "../../../../client/utils";
import { ProductFinancialSector } from "../../../../product/enums";
import { MediationClientFormType } from "../../../enums";
import { CreateUpdateFinancialMediation, FinancialMediationFormClients } from "../../../types";
import {
  FINANCIAL_MEDIATION_CHILDREN_SECTORS,
  FINANCIAL_MEDIATION_CLIENTS_WITH_ID_CARD,
  FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS,
} from "../../../utils";

interface ClientsStagesState {
  policyHolder?: ClientFormStage;
  secondaryClient?: ClientFormStage;
  policyHolderRepresentative?: ClientFormStage;
  secondaryClientRepresentative?: ClientFormStage;
  child1?: ClientFormStage;
  child2?: ClientFormStage;
  child3?: ClientFormStage;
  child4?: ClientFormStage;
}

interface ClientRepresentativeSelectData {
  type: MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE | MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE;
  client: LegalClient;
}

type MediationClientFormTypeIdentifier = keyof Pick<
  CreateUpdateFinancialMediation,
  | "policyHolderIdentifier"
  | "secondaryClientIdentifier"
  | "policyHolderRepresentativeIdentifier"
  | "secondaryClientRepresentativeIdentifier"
  | "child1Identifier"
  | "child2Identifier"
  | "child3Identifier"
  | "child4Identifier"
>;

const noRepeatedClientRule =
  (checkedType: MediationClientFormType): Rule =>
  ({ getFieldsValue }) => ({
    validator: (_, value) => {
      if (value) {
        const formValues = getFieldsValue() as CreateUpdateFinancialMediation;

        return (checkedType !== MediationClientFormType.POLICY_HOLDER && value === formValues.policyHolderIdentifier) ||
          (checkedType !== MediationClientFormType.SECONDARY_CLIENT &&
            value === formValues.secondaryClientIdentifier) ||
          (checkedType !== MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE &&
            value === formValues.policyHolderRepresentativeIdentifier) ||
          (checkedType !== MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE &&
            value === formValues.secondaryClientRepresentativeIdentifier) ||
          (checkedType !== MediationClientFormType.CHILD_1 && value === formValues.child1Identifier) ||
          (checkedType !== MediationClientFormType.CHILD_2 && value === formValues.child2Identifier) ||
          (checkedType !== MediationClientFormType.CHILD_3 && value === formValues.child3Identifier) ||
          (checkedType !== MediationClientFormType.CHILD_4 && value === formValues.child4Identifier)
          ? Promise.reject(t("financialMediation.validations.noRepeatedClient"))
          : Promise.resolve();
      }

      return Promise.resolve();
    },
  });

const clientYoungerThan18Rule: Rule = {
  validator: (_, value) =>
    value && validationFunctions.validatePin(value) && dayjs().diff(parseBirthDateFromPin(value), "years") >= 18
      ? Promise.reject(t("financialMediation.validations.clientYoungerThan18"))
      : Promise.resolve(),
};

const colSpan = 4;

interface Props {
  form: FormInstance<CreateUpdateFinancialMediation>;
  formDisabled?: boolean;
  sector: ProductFinancialSector;
  clients: FinancialMediationFormClients;
  clientsViolationErrors: Map<MediationClientFormType, FieldConstraintViolation[]>;
  secondaryClientActive: boolean;
  childrenCount: number;
  representative1Active: boolean;
  representative2Active: boolean;
  validateAllFields?: boolean;
  onClientChange: (type: MediationClientFormType, client?: Partial<Client>) => void;
  onClientViolationErrorsChange: (type: MediationClientFormType, violations?: FieldConstraintViolation[]) => void;
  onSecondaryClientActiveToggle: () => void;
  onChildAdd: () => void;
  onChildRemove: () => void;
  onRepresentative1Toggle: () => void;
  onRepresentative2Toggle: () => void;
  onClientFormValuesChange: () => void;
}

export const FinancialMediationFormClientsSection = ({ form, formDisabled, sector, clients, ...props }: Props) => {
  const { t } = useTranslation();

  const clientSearch = useClientSearch();

  const [clientFormOpen, setClientFormOpen] = useState<boolean>(false);
  const [processedClientFormType, setProcessedClientFormType] = useState<MediationClientFormType>();
  const [clientStages, setClientStages] = useState<ClientsStagesState>({
    policyHolder: clients.policyHolder ? ClientFormStage.SELECTED : undefined,
    secondaryClient: clients.secondaryClient ? ClientFormStage.SELECTED : undefined,
    policyHolderRepresentative: clients.policyHolderRepresentative ? ClientFormStage.SELECTED : undefined,
    secondaryClientRepresentative: clients.secondaryClientRepresentative ? ClientFormStage.SELECTED : undefined,
    child1: clients.child1 ? ClientFormStage.SELECTED : undefined,
    child2: clients.child2 ? ClientFormStage.SELECTED : undefined,
    child3: clients.child3 ? ClientFormStage.SELECTED : undefined,
    child4: clients.child4 ? ClientFormStage.SELECTED : undefined,
  });

  const [representativeSelectData, setRepresentativeSelectData] = useState<ClientRepresentativeSelectData>();

  const getClientFromProps = (type: MediationClientFormType) => {
    switch (type) {
      case MediationClientFormType.POLICY_HOLDER:
        return clients.policyHolder;
      case MediationClientFormType.SECONDARY_CLIENT:
        return clients.secondaryClient;
      case MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE:
        return clients.policyHolderRepresentative;
      case MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE:
        return clients.secondaryClientRepresentative;
      case MediationClientFormType.CHILD_1:
        return clients.child1;
      case MediationClientFormType.CHILD_2:
        return clients.child2;
      case MediationClientFormType.CHILD_3:
        return clients.child3;
      case MediationClientFormType.CHILD_4:
        return clients.child4;
      default:
        return undefined;
    }
  };

  const getClientFormIdentifier = (type: MediationClientFormType): string | undefined => {
    const formIdentifier = resolveClientFormTypeIdentifierName(type);
    return formIdentifier ? form.getFieldValue(formIdentifier) : undefined;
  };

  const getClientFormStage = (type: MediationClientFormType): ClientFormStage | undefined => {
    switch (type) {
      case MediationClientFormType.POLICY_HOLDER:
        return clientStages.policyHolder;
      case MediationClientFormType.SECONDARY_CLIENT:
        return clientStages.secondaryClient;
      case MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE:
        return clientStages.policyHolderRepresentative;
      case MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE:
        return clientStages.secondaryClientRepresentative;
      case MediationClientFormType.CHILD_1:
        return clientStages.child1;
      case MediationClientFormType.CHILD_2:
        return clientStages.child2;
      case MediationClientFormType.CHILD_3:
        return clientStages.child3;
      case MediationClientFormType.CHILD_4:
        return clientStages.child4;
      default:
        return undefined;
    }
  };

  const setClientFormStage = (type: MediationClientFormType, stage?: ClientFormStage) => {
    switch (type) {
      case MediationClientFormType.POLICY_HOLDER:
        setClientStages({ ...clientStages, policyHolder: stage });
        break;
      case MediationClientFormType.SECONDARY_CLIENT:
        setClientStages({ ...clientStages, secondaryClient: stage });
        break;
      case MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE:
        setClientStages({ ...clientStages, policyHolderRepresentative: stage });
        break;
      case MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE:
        setClientStages({ ...clientStages, secondaryClientRepresentative: stage });
        break;
      case MediationClientFormType.CHILD_1:
        setClientStages({ ...clientStages, child1: stage });
        break;
      case MediationClientFormType.CHILD_2:
        setClientStages({ ...clientStages, child2: stage });
        break;
      case MediationClientFormType.CHILD_3:
        setClientStages({ ...clientStages, child3: stage });
        break;
      case MediationClientFormType.CHILD_4:
        setClientStages({ ...clientStages, child4: stage });
        break;
    }
  };

  const resolveClientFormTypeIdentifierName = (
    type: MediationClientFormType,
  ): MediationClientFormTypeIdentifier | undefined => {
    switch (type) {
      case MediationClientFormType.POLICY_HOLDER:
        return "policyHolderIdentifier";
      case MediationClientFormType.SECONDARY_CLIENT:
        return "secondaryClientIdentifier";
      case MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE:
        return "policyHolderRepresentativeIdentifier";
      case MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE:
        return "secondaryClientRepresentativeIdentifier";
      case MediationClientFormType.CHILD_1:
        return "child1Identifier";
      case MediationClientFormType.CHILD_2:
        return "child2Identifier";
      case MediationClientFormType.CHILD_3:
        return "child3Identifier";
      case MediationClientFormType.CHILD_4:
        return "child4Identifier";
      default:
        return undefined;
    }
  };

  const resolveClientTypeByClientFormType = (type: MediationClientFormType): ClientType | undefined => {
    switch (type) {
      case MediationClientFormType.POLICY_HOLDER:
      case MediationClientFormType.SECONDARY_CLIENT:
        return undefined;
      case MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE:
      case MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE:
      case MediationClientFormType.CHILD_1:
      case MediationClientFormType.CHILD_2:
      case MediationClientFormType.CHILD_3:
      case MediationClientFormType.CHILD_4:
        return ClientType.NATURAL;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    return () => {
      clientSearch.onResultDelete();
    };
  }, []);

  useEffect(() => {
    if (processedClientFormType && clientSearch.result.keyword === getClientFormIdentifier(processedClientFormType)) {
      if (clientSearch.result.data) {
        setClientFormOpen(true);
        setClientFormStage(processedClientFormType, ClientFormStage.EXISTING);
        if (
          clientSearch.result.data.type === ClientType.NATURAL ||
          clientSearch.result.data.type === ClientType.SELF_EMPLOYED
        ) {
          props.onClientChange(processedClientFormType, {
            ...clientSearch.result.data,
            identityCardNumber: undefined,
            previousIdentityCardNumber: (clientSearch.result.data as NaturalClient).identityCardNumber,
          } as NaturalClient);
        } else {
          props.onClientChange(processedClientFormType, clientSearch.result.data);
        }
      } else {
        setClientFormStage(processedClientFormType, ClientFormStage.NEW);
      }
    }
  }, [clientSearch.result]);

  const handleClientSearchActionClick = (type: MediationClientFormType, actionType: ClientSearchActionType) => {
    switch (actionType) {
      case ClientSearchActionType.CREATE:
      case ClientSearchActionType.UPDATE:
        setProcessedClientFormType(type);
        setClientFormOpen(true);
        break;
      case ClientSearchActionType.DELETE:
        if (
          type === MediationClientFormType.POLICY_HOLDER ||
          type === MediationClientFormType.SECONDARY_CLIENT ||
          type === MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE ||
          type === MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE
        ) {
          const clientIdentifier = getClientFormIdentifier(type);
          const {
            child1RepresentativeIdentifier,
            child2RepresentativeIdentifier,
            child3RepresentativeIdentifier,
            child4RepresentativeIdentifier,
          } = form.getFieldsValue([
            ["child1RepresentativeIdentifier"],
            ["child2RepresentativeIdentifier"],
            ["child3RepresentativeIdentifier"],
            ["child4RepresentativeIdentifier"],
          ]) as CreateUpdateFinancialMediation;

          form.setFieldsValue({
            child1RepresentativeIdentifier:
              clientIdentifier === child1RepresentativeIdentifier ? undefined : child1RepresentativeIdentifier,
            child2RepresentativeIdentifier:
              clientIdentifier === child2RepresentativeIdentifier ? undefined : child2RepresentativeIdentifier,
            child3RepresentativeIdentifier:
              clientIdentifier === child3RepresentativeIdentifier ? undefined : child3RepresentativeIdentifier,
            child4RepresentativeIdentifier:
              clientIdentifier === child4RepresentativeIdentifier ? undefined : child4RepresentativeIdentifier,
          });
        }

        const clientFormTypeIdentifier = resolveClientFormTypeIdentifierName(type);

        if (clientFormTypeIdentifier) {
          form.setFieldsValue({ [clientFormTypeIdentifier]: undefined });
        }
        setClientFormStage(type);
        setProcessedClientFormType(undefined);
        props.onClientViolationErrorsChange(type);
        props.onClientChange(type);
        break;
    }
  };

  const handleClientSearchSubmit = (value: string, type: MediationClientFormType) => {
    form
      .validateFields([resolveClientFormTypeIdentifierName(type)])
      .then(() => clientSearch.onSearch({ keyword: value, clientType: resolveClientTypeByClientFormType(type) }))
      .catch(resolveFormValidationError);
  };

  const handleClientSearchChange = (value: string, type: MediationClientFormType) => {
    if (getClientFormStage(type)) {
      setClientFormStage(type);
    }
    if (getClientFromProps(type)) {
      props.onClientChange(type);
    }
    if (props.clientsViolationErrors.has(type)) {
      props.onClientViolationErrorsChange(type);
    }

    handleClientSearchSubmit(value, type);
  };

  const handleClientFormSubmit = (client: Client, clientFormType: MediationClientFormType) => {
    setClientFormOpen(false);
    setProcessedClientFormType(undefined);
    setClientFormStage(clientFormType, ClientFormStage.SELECTED);
    props.onClientViolationErrorsChange(clientFormType);
    props.onClientChange(clientFormType, client);
  };

  const handleClientRepresentativeSelect = (clientRepresentative: ClientRepresentative) => {
    const representative = clientRepresentative.representative;
    if (!representativeSelectData) {
      return;
    }

    const representativeType = representativeSelectData.type;

    setRepresentativeSelectData(undefined);
    setProcessedClientFormType(representativeType);

    setClientFormOpen(true);
    setClientFormStage(representativeType, ClientFormStage.EXISTING);

    props.onClientChange(representativeType, {
      ...representative,
      identityCardNumber: undefined,
      previousIdentityCardNumber: representative.identityCardNumber,
    } as NaturalClient);

    switch (representativeType) {
      case MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE:
        form.setFieldsValue({
          policyHolderRepresentativeIdentifier: representative.identifier,
          mediationData: { policyHolderData: { representativeFunction: clientRepresentative.function } },
        });
        break;
      case MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE:
        form.setFieldsValue({
          secondaryClientRepresentativeIdentifier: representative.identifier,
          mediationData: { secondaryClientData: { representativeFunction: clientRepresentative.function } },
        });
        break;
    }
  };

  const clientSearchProps = {
    processedType: processedClientFormType,
    violationErrors: props.clientsViolationErrors,
    inProgress: clientSearch.inProgress,
    disabled: formDisabled,
    inputColSpan: 5,
    clientNameColSpan: 6,
    onActionClick: handleClientSearchActionClick,
    onFocus: setProcessedClientFormType,
    onSearch: handleClientSearchSubmit,
    onChange: handleClientSearchChange,
  };

  return (
    <>
      <Card type="inner" className="card-box" title={t("financialMediation.sections.clients")}>
        <Row gutter={rowGutter}>
          <ClientSearchInput<MediationClientFormType>
            {...clientSearchProps}
            formItemProps={{
              name: "policyHolderIdentifier",
              label: (
                <LabelWithTooltip
                  tooltip={t("helpers.pinFormat")}
                  label={t("financialMediation.attrs.policyHolderIdentifier")}
                />
              ),
              rules: [
                validations.notBlank,
                validations.pinOrCrn,
                noRepeatedClientRule(MediationClientFormType.POLICY_HOLDER),
              ],
              validateFirst: true,
            }}
            formStage={clientStages.policyHolder}
            formType={MediationClientFormType.POLICY_HOLDER}
            client={clients.policyHolder}
          />

          <Col span={props.secondaryClientActive ? colSpan - 1 : colSpan}>
            <ContactsEmailAutoComplete
              formItemProps={{
                name: ["mediationData", "policyHolderData", "email"],
                label: t("financialMediation.attrs.mediationData.policyHolderData.email"),
                rules: [validations.notBlank, validations.size(1, 254), validations.email],
              }}
              contacts={clients.policyHolder?.contacts}
            />
          </Col>

          <Col span={props.secondaryClientActive ? colSpan - 1 : colSpan}>
            <ContactsPhoneNumberAutoComplete
              formItemProps={{
                name: ["mediationData", "policyHolderData", "phone"],
                label: t("financialMediation.attrs.mediationData.policyHolderData.phone"),
                rules: [validations.notBlank, validations.mobilePhoneNumber()],
                normalize: phoneNumberNormalizeFunction,
              }}
              contacts={clients.policyHolder?.contacts}
            />
          </Col>

          <Col span={colSpan}>
            <MarketingConsentSelect
              formItemProps={{
                name: ["mediationData", "policyHolderData", "marketingConsent"],
                label: (
                  <LabelWithPopover
                    label={t("contact.enums.marketingConsent._label")}
                    popoverTitle={t("financialMediation.helpers.marketingConsentDesc.title")}
                    popoverContent={
                      <div style={{ maxWidth: "550px", maxHeight: "450px", overflowY: "scroll" }}>
                        {[1, 2, 3, 4, 5].map((value) => (
                          <p key={value}>{t(`financialMediation.helpers.marketingConsentDesc.p${value}`)}</p>
                        ))}
                      </div>
                    }
                  />
                ),
                rules: [validations.notNull],
              }}
            />
          </Col>

          {props.secondaryClientActive && (
            <Col span={colSpan - 1}>
              <Form.Item
                name={["mediationData", "policyHolderData", "clientRelationship"]}
                label={t("financialMediation.attrs.mediationData.policyHolderData.clientRelationship")}
                rules={[validations.size(1, 128)]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>

        {FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector) && (
          <>
            {props.secondaryClientActive && (
              <Row gutter={rowGutter}>
                <ClientSearchInput<MediationClientFormType>
                  {...clientSearchProps}
                  formItemProps={{
                    name: "secondaryClientIdentifier",
                    label: (
                      <LabelWithTooltip
                        tooltip={t("helpers.pinFormat")}
                        label={t("financialMediation.attrs.secondaryClientIdentifier")}
                      />
                    ),
                    rules: [
                      validations.notBlank,
                      validations.pinOrCrn,
                      noRepeatedClientRule(MediationClientFormType.SECONDARY_CLIENT),
                    ],
                    validateFirst: true,
                  }}
                  formStage={clientStages.secondaryClient}
                  formType={MediationClientFormType.SECONDARY_CLIENT}
                  client={clients.secondaryClient}
                />

                <Col span={colSpan - 1}>
                  <ContactsEmailAutoComplete
                    formItemProps={{
                      name: ["mediationData", "secondaryClientData", "email"],
                      label: t("financialMediation.attrs.mediationData.secondaryClientData.email"),
                      rules: [validations.notBlank, validations.size(1, 254), validations.email],
                    }}
                    contacts={clients.secondaryClient?.contacts}
                  />
                </Col>

                <Col span={colSpan - 1}>
                  <ContactsPhoneNumberAutoComplete
                    formItemProps={{
                      name: ["mediationData", "secondaryClientData", "phone"],
                      label: t("financialMediation.attrs.mediationData.secondaryClientData.phone"),
                      rules: [validations.notBlank, validations.mobilePhoneNumber()],
                      normalize: phoneNumberNormalizeFunction,
                    }}
                    contacts={clients.secondaryClient?.contacts}
                  />
                </Col>

                <Col span={colSpan}>
                  <MarketingConsentSelect
                    formItemProps={{
                      name: ["mediationData", "secondaryClientData", "marketingConsent"],
                      label: (
                        <LabelWithPopover
                          label={t("contact.enums.marketingConsent._label")}
                          popoverTitle={t("financialMediation.helpers.marketingConsentDesc.title")}
                          popoverContent={
                            <div style={{ maxWidth: "550px", maxHeight: "450px", overflowY: "scroll" }}>
                              {[1, 2, 3, 4, 5].map((value) => (
                                <p key={value}>{t(`financialMediation.helpers.marketingConsentDesc.p${value}`)}</p>
                              ))}
                            </div>
                          }
                        />
                      ),
                      rules: [validations.notNull],
                    }}
                  />
                </Col>

                <Col span={colSpan - 1}>
                  <Form.Item
                    name={["mediationData", "secondaryClientData", "clientRelationship"]}
                    label={t("financialMediation.attrs.mediationData.secondaryClientData.clientRelationship")}
                    rules={[validations.size(1, 128)]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {!formDisabled && (
              <Row gutter={rowGutter} className="margin-bottom-medium">
                <Col span={colSpan}>
                  <Space>
                    <ActionButton
                      icon="plus"
                      disabled={props.secondaryClientActive}
                      onClick={props.onSecondaryClientActiveToggle}
                    >
                      {t("financialMediation.actions.addSecondaryClient")}
                    </ActionButton>

                    <ActionButton
                      icon="minus"
                      disabled={!props.secondaryClientActive}
                      onClick={props.onSecondaryClientActiveToggle}
                    >
                      {t("financialMediation.actions.removeSecondaryClient")}
                    </ActionButton>
                  </Space>
                </Col>
              </Row>
            )}
          </>
        )}

        {(clients.policyHolder ||
          (FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector) &&
            props.secondaryClientActive &&
            clients.secondaryClient)) &&
          (!formDisabled || clients.policyHolderRepresentative || clients.secondaryClientRepresentative) && (
            <>
              <Divider titlePlacement="start" className="divider-subheader">
                {t("financialMediation.sections.representatives")}
              </Divider>

              {clients.policyHolder && (
                <>
                  <Row gutter={rowGutter}>
                    <Col span={colSpan * 2}>
                      {clients.policyHolder.type === ClientType.NATURAL ||
                      clients.policyHolder.type === ClientType.SELF_EMPLOYED ? (
                        <Space className="margin-bottom-tiny">
                          <b>{t("financialMediation.attrs.policyHolderRepresentativeNatural")}: </b>
                          {!formDisabled && (
                            <>
                              <ActionButton
                                icon="plus"
                                disabled={props.representative1Active}
                                onClick={props.onRepresentative1Toggle}
                              >
                                {t("common.add")}
                              </ActionButton>

                              <ActionButton
                                icon="minus"
                                disabled={!props.representative1Active}
                                onClick={props.onRepresentative1Toggle}
                              >
                                {t("common.remove")}
                              </ActionButton>
                            </>
                          )}
                        </Space>
                      ) : (
                        <div className="margin-bottom-tiny bold-text">
                          {t("financialMediation.attrs.policyHolderRepresentative")}
                        </div>
                      )}
                    </Col>
                  </Row>

                  {(!formDisabled || clients.policyHolderRepresentative) &&
                    (props.representative1Active || clients.policyHolder.type === ClientType.LEGAL) && (
                      <Row gutter={rowGutter}>
                        {clients.policyHolder.type === ClientType.LEGAL && (
                          <>
                            {!formDisabled && (clients.policyHolder as LegalClient).representatives?.length ? (
                              <Col style={{ paddingTop: "32px" }}>
                                <SecondaryButton
                                  size="small"
                                  onClick={() =>
                                    setRepresentativeSelectData({
                                      type: MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE,
                                      client: clients.policyHolder as LegalClient,
                                    })
                                  }
                                >
                                  {t("financialMediation.actions.selectRepresentative")}
                                </SecondaryButton>
                              </Col>
                            ) : undefined}

                            <Col span={colSpan}>
                              <Form.Item
                                name={["mediationData", "policyHolderData", "representativeFunction"]}
                                label={t("financialMediation.helpers.policyHolderRepresentativeFunction")}
                                rules={[validations.notNull]}
                              >
                                <Select
                                  {...selectStandardProps}
                                  options={Object.keys(ClientRepresentativeFunction).map((func) => ({
                                    value: func,
                                    label: t("client.enums.representativeFunction." + func),
                                  }))}
                                />
                              </Form.Item>
                            </Col>
                          </>
                        )}

                        <ClientSearchInput<MediationClientFormType>
                          {...clientSearchProps}
                          formItemProps={{
                            name: "policyHolderRepresentativeIdentifier",
                            label: (
                              <LabelWithTooltip
                                tooltip={t("helpers.pinFormat")}
                                label={t("financialMediation.attrs.policyHolderRepresentativeIdentifier")}
                              />
                            ),
                            rules: [
                              validations.notBlank,
                              validations.pin,
                              noRepeatedClientRule(MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE),
                            ],
                            validateFirst: true,
                          }}
                          formStage={getClientFormStage(MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE)}
                          formType={MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE}
                          client={getClientFromProps(MediationClientFormType.POLICY_HOLDER_REPRESENTATIVE)}
                        />
                      </Row>
                    )}
                </>
              )}

              {FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector) &&
                props.secondaryClientActive &&
                clients.secondaryClient && (
                  <div className="margin-top-small">
                    <Row gutter={rowGutter}>
                      <Col span={colSpan * 2}>
                        {clients.secondaryClient.type === ClientType.NATURAL ||
                        clients.secondaryClient.type === ClientType.SELF_EMPLOYED ? (
                          <Space className="margin-bottom-tiny">
                            <b>{t("financialMediation.attrs.secondaryClientRepresentativeNatural")}: </b>
                            {!formDisabled && (
                              <>
                                <ActionButton
                                  icon="plus"
                                  disabled={props.representative2Active}
                                  onClick={props.onRepresentative2Toggle}
                                >
                                  {t("common.add")}
                                </ActionButton>

                                <ActionButton
                                  icon="minus"
                                  disabled={!props.representative2Active}
                                  onClick={props.onRepresentative2Toggle}
                                >
                                  {t("common.remove")}
                                </ActionButton>
                              </>
                            )}
                          </Space>
                        ) : (
                          <div className="margin-bottom-tiny bold-text">
                            {t("financialMediation.attrs.secondaryClientRepresentative")}
                          </div>
                        )}
                      </Col>
                    </Row>

                    {(!formDisabled || clients.secondaryClientRepresentative) &&
                      (props.representative2Active || clients.secondaryClient.type === ClientType.LEGAL) && (
                        <Row gutter={rowGutter}>
                          {clients.secondaryClient.type === ClientType.LEGAL && (
                            <>
                              {!formDisabled && (clients.secondaryClient as LegalClient).representatives?.length ? (
                                <Col style={{ paddingTop: "32px" }}>
                                  <SecondaryButton
                                    size="small"
                                    onClick={() =>
                                      setRepresentativeSelectData({
                                        type: MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE,
                                        client: clients.secondaryClient as LegalClient,
                                      })
                                    }
                                  >
                                    {t("financialMediation.actions.selectRepresentative")}
                                  </SecondaryButton>
                                </Col>
                              ) : undefined}

                              <Col span={colSpan}>
                                <Form.Item
                                  name={["mediationData", "secondaryClientData", "representativeFunction"]}
                                  label={t("financialMediation.helpers.secondaryClientRepresentativeFunction")}
                                  rules={[validations.notNull]}
                                >
                                  <Select
                                    {...selectStandardProps}
                                    options={Object.keys(ClientRepresentativeFunction).map((func) => ({
                                      value: func,
                                      label: t("client.enums.representativeFunction." + func),
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                            </>
                          )}

                          <ClientSearchInput<MediationClientFormType>
                            {...clientSearchProps}
                            formItemProps={{
                              name: "secondaryClientRepresentativeIdentifier",
                              label: (
                                <LabelWithTooltip
                                  tooltip={t("helpers.pinFormat")}
                                  label={t("financialMediation.attrs.secondaryClientRepresentativeIdentifier")}
                                />
                              ),
                              rules: [
                                validations.notBlank,
                                validations.pin,
                                noRepeatedClientRule(MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE),
                              ],
                              validateFirst: true,
                            }}
                            formStage={getClientFormStage(MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE)}
                            formType={MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE}
                            client={getClientFromProps(MediationClientFormType.SECONDARY_CLIENT_REPRESENTATIVE)}
                          />
                        </Row>
                      )}
                  </div>
                )}
            </>
          )}

        {FINANCIAL_MEDIATION_CHILDREN_SECTORS.includes(sector) &&
          (clients.policyHolder?.type === ClientType.NATURAL ||
            (FINANCIAL_MEDIATION_SECONDARY_CLIENT_SECTORS.includes(sector) &&
              props.secondaryClientActive &&
              clients.secondaryClient?.type === ClientType.NATURAL)) &&
          (!formDisabled || clients.child1) && (
            <>
              <Divider titlePlacement="start" className="divider-subheader">
                {t("financialMediation.sections.children")}
              </Divider>

              {props.childrenCount > 0 && (
                <>
                  {Array.from({ length: props.childrenCount }, (_, i) => i + 1).map((i) => {
                    const childType = `CHILD_${i}` as MediationClientFormType;
                    return (
                      <Row gutter={rowGutter} key={i}>
                        <ClientSearchInput<MediationClientFormType>
                          {...clientSearchProps}
                          formItemProps={{
                            name: `child${i}Identifier`,
                            label: (
                              <LabelWithTooltip
                                tooltip={t("helpers.pinFormat")}
                                label={t(`financialMediation.attrs.child${i}Identifier`)}
                              />
                            ),
                            rules: [
                              validations.notBlank,
                              validations.pin,
                              noRepeatedClientRule(childType),
                              clientYoungerThan18Rule,
                            ],
                            validateFirst: true,
                          }}
                          formStage={getClientFormStage(childType)}
                          formType={childType}
                          client={getClientFromProps(childType)}
                        />

                        <Col span={5}>
                          <Form.Item
                            name={`child${i}RepresentativeIdentifier`}
                            label={t(`financialMediation.attrs.child${i}RepresentativeIdentifier`)}
                            rules={[validations.notNull]}
                          >
                            <Select
                              {...selectStandardProps}
                              options={[
                                clients.policyHolder,
                                clients.secondaryClient,
                                clients.policyHolderRepresentative,
                                clients.secondaryClientRepresentative,
                              ]
                                .filter((client) => client?.type === ClientType.NATURAL)
                                .map((client) => ({
                                  value: client?.identifier,
                                  label: formatClientName(client as ClientBase),
                                }))}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={colSpan}>
                          <Form.Item
                            name={["mediationData", "childrenRelationships", i - 1]}
                            label={t("financialMediation.attrs.mediationData.childrenRelationships")}
                            rules={[validations.size(1, 128)]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  })}
                </>
              )}

              {!formDisabled && (
                <Row gutter={rowGutter} className="margin-bottom-medium">
                  <Col span={colSpan}>
                    <Space>
                      <ActionButton icon="plus" disabled={props.childrenCount >= 4} onClick={props.onChildAdd}>
                        {t("common.add")}
                      </ActionButton>

                      <ActionButton icon="minus" disabled={props.childrenCount <= 0} onClick={props.onChildRemove}>
                        {t("common.remove")}
                      </ActionButton>
                    </Space>
                  </Col>
                </Row>
              )}
            </>
          )}
      </Card>

      <ClientDrawerForm<MediationClientFormType>
        open={clientFormOpen}
        client={processedClientFormType ? getClientFromProps(processedClientFormType) : undefined}
        initialClientType={
          processedClientFormType ? resolveClientTypeByClientFormType(processedClientFormType) : undefined
        }
        initialIdentifier={processedClientFormType ? getClientFormIdentifier(processedClientFormType) : undefined}
        requireIdCardNumber={
          processedClientFormType
            ? FINANCIAL_MEDIATION_CLIENTS_WITH_ID_CARD.includes(processedClientFormType)
            : undefined
        }
        formType={processedClientFormType}
        disabled={formDisabled}
        violationErrors={props.clientsViolationErrors}
        onFormSubmit={handleClientFormSubmit}
        onFormValuesChange={props.onClientFormValuesChange}
      />

      <ClientRepresentativeSelectModal
        open={!!representativeSelectData}
        client={representativeSelectData?.client}
        onSelect={handleClientRepresentativeSelect}
        onFormCancel={() => setRepresentativeSelectData(undefined)}
      />
    </>
  );
};
